import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone, X, Delete, Users, Zap } from 'lucide-react';
import { SpeedDials } from '@/lib/api';
import { useSoftphone } from '@/store/softphone';
import clsx from 'clsx';

/**
 * Hızlı arama popover'ı. Topbar Grid3x3 ikonunun altına anchor olur.
 *
 * Mevcut `/quick-dial` sayfasının kompakt versiyonu — agent çoğu zaman
 * sadece bir numara çevirip aramak ister, ayrı sayfaya gitmesi gereksiz.
 * Bu popover Aktar popover'ı ile aynı pattern'i kullanır.
 *
 * Yapı:
 *  - Numara input (autoFocus) + backspace
 *  - Hızlı dahili listesi (varsa) — tek tıkla ara
 *  - 3×4 keypad — büyük tuşlar (44px)
 *  - "Ara" butonu altta — state=registered + numara dolu ise enabled
 *
 * State=in_call iken keypad'in DTMF göndermesi yok (Aktar popover'ında
 * zaten var). Sadece numara çevirmek için.
 */
export function QuickDialPopover() {
  const dial = useSoftphone((s) => s.dial);
  const callState = useSoftphone((s) => s.state);
  const close = useSoftphone((s) => s.closeQuickDial);
  const open = useSoftphone((s) => s.quickDialOpen);
  const anchor = useSoftphone((s) => s.quickDialAnchor);

  const [number, setNumber] = useState('');
  const numberRef = useRef('');
  useEffect(() => { numberRef.current = number; }, [number]);
  const { data: speedDials } = useQuery({
    queryKey: ['speed-dials'],
    queryFn: SpeedDials.list,
    enabled: open,
  });

  // Klavyeden tuşlama — popover açıkken rakam/yıldız/diye tuşları yakala.
  // Numara alanını salt-görüntü yaptık (çift sayım olmasın); tek giriş noktası bu.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Backspace') { setNumber((n) => n.slice(0, -1)); e.preventDefault(); return; }
      if (e.key === 'Enter') {
        const n = numberRef.current.trim();
        if (callState === 'registered' && n) { dial(n); setNumber(''); close(); }
        return;
      }
      if (/^[0-9*#+]$/.test(e.key)) { setNumber((n) => n + e.key); e.preventDefault(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, callState, dial, close]);

  // Click-outside + Esc
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('[data-quickdial-popover]') && !el.closest('[data-quickdial-trigger]')) {
        close();
      }
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const id = setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onEsc);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, close]);

  // Açılınca number'ı sıfırla (önceki kalıntı kalmasın)
  useEffect(() => {
    if (!open) setNumber('');
  }, [open]);

  if (!open) return null;

  const trimmed = number.trim();
  const canCall = callState === 'registered' && !!trimmed;

  const handleDial = (n: string) => {
    if (callState !== 'registered') return;
    dial(n);
    setNumber('');
    close();
  };
  const padKey = (k: string) => setNumber((n) => n + k);
  const backspace = () => setNumber((n) => n.slice(0, -1));

  const positionStyle: React.CSSProperties = anchor
    ? { position: 'fixed', top: anchor.top, right: anchor.right, zIndex: 50 }
    : { position: 'fixed', top: 64, right: 20, zIndex: 50 };

  return (
    <div
      data-quickdial-popover
      style={positionStyle}
      className="w-[320px] bg-surface rounded-2xl shadow-[0_18px_50px_-12px_rgba(15,23,42,0.25)] ring-1 ring-black/5 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100">
        <h3 className="text-[13px] font-semibold text-slate-900 tracking-tight flex items-center gap-1.5">
          <Zap size={13} className="text-brand-500" /> Hızlı Arama
        </h3>
        <button onClick={close} className="p-1 text-slate-400 hover:text-slate-700 rounded">
          <X size={14} />
        </button>
      </div>

      <div className="p-3">
        {/* Number input */}
        <div className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100/70 rounded-lg ring-1 ring-transparent focus-within:bg-surface focus-within:ring-brand-300 transition-all">
          <Phone size={12} className="text-slate-400 flex-shrink-0" />
          {/* Salt-görüntü — giriş klavyeden (global handler) veya keypad'den.
              Gerçek <input> yerine div: bazı durumlarda focus alamayıp klavye
              tuşlaması çalışmıyordu; global keydown her zaman yakalar. */}
          <div className="flex-1 min-w-0 text-[14px] font-mono tracking-wider text-slate-800 truncate">
            {number || <span className="font-sans text-slate-400 text-[12px]">Numara veya extension</span>}
          </div>
          <button
            onClick={backspace}
            disabled={!number}
            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-25 rounded transition"
            title="Sil"
          >
            <Delete size={12} />
          </button>
        </div>

        {/* Speed dials (varsa) */}
        {speedDials && speedDials.length > 0 && (
          <div className="mt-2.5">
            <div className="flex items-center justify-between px-1 mb-1">
              <span className="flex items-center gap-1 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <Users size={10} className="text-slate-400" />
                Hızlı Dahili
              </span>
              <span className="text-[10px] text-slate-400">{speedDials.length}</span>
            </div>
            <ul className="bg-slate-50/60 rounded-lg ring-1 ring-slate-200/70 divide-y divide-slate-100 max-h-[100px] overflow-y-auto">
              {speedDials.map((sd) => (
                <li key={sd.id}>
                  <button
                    onClick={() => handleDial(sd.number)}
                    disabled={callState !== 'registered'}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-surface active:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition text-left"
                  >
                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Phone size={10} className="text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-medium text-slate-800 truncate">{sd.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{sd.number}</div>
                    </div>
                    <Phone size={10} className="text-slate-300 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-1 mt-2.5">
          {['1','2','3','4','5','6','7','8','9','*','0','#'].map((k) => (
            <button
              key={k}
              onClick={() => padKey(k)}
              className="h-10 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-800 text-[15px] font-medium active:bg-slate-200 active:scale-[0.97] transition-all duration-100"
            >
              {k}
            </button>
          ))}
        </div>

        {/* Call button */}
        <button
          onClick={() => canCall && handleDial(trimmed)}
          disabled={!canCall}
          className={clsx(
            'w-full mt-2.5 py-2 rounded-lg text-[12.5px] font-semibold transition flex items-center justify-center gap-1.5',
            canCall
              ? 'bg-emerald-500 hover:bg-emerald-600 text-[#08140d] shadow-[0_4px_12px_-3px_rgba(16,185,129,0.5)]'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed',
          )}
        >
          <Phone size={12} fill={canCall ? 'currentColor' : 'none'} />
          {callState !== 'registered' ? 'SIP bağlı değil' : 'Ara'}
        </button>
      </div>
    </div>
  );
}
